import { VisitStatus } from '@core/enums/visit-status.enum';
import { FlowId, type CheckinWorkflow } from '../workflow-engine.types';
import { checkinFlow } from './checkin.flow';
import { checkinSummaryFlow } from './checkin-summary.flow';
import { facialRecognitionFlow } from './facial-recognition.flow';
import { printBadgeFlow } from './print-badge.flow';
import { traineeTypeSelectionFlow } from './trainee-type-selection.flow';
import { trainingSummaryFlow } from './training-summary.flow';

export enum PreCheckinStep {
  Start = 'Start',
  AfterTraineeType = 'AfterTraineeType',
  TrainingSummary = 'TrainingSummary',
  AfterTrainingSummary = 'AfterTrainingSummary',
  AfterFacialRecognition = 'AfterFacialRecognition',
  Checkin = 'Checkin',
  AfterCheckin = 'AfterCheckin',
  AfterPrintBadge = 'AfterPrintBadge',
  AfterCheckinSummary = 'AfterCheckinSummary',
}

export const preCheckinFlow: CheckinWorkflow<PreCheckinStep> = {
  id: FlowId.PreCheckin,
  entry: PreCheckinStep.Start,
  steps: {
    [PreCheckinStep.Start]: {
      id: PreCheckinStep.Start,
      next: (variables) =>
        variables.visitStatus === VisitStatus.DRY_PRECHECKED_IN
          ? { flow: traineeTypeSelectionFlow, returnStep: PreCheckinStep.AfterTraineeType }
          : { nextStep: PreCheckinStep.TrainingSummary },
      back: () => ({ bubble: 'back' }),
    },
    [PreCheckinStep.AfterTraineeType]: {
      id: PreCheckinStep.AfterTraineeType,
      next: () => ({ nextStep: PreCheckinStep.TrainingSummary }),
    },
    [PreCheckinStep.TrainingSummary]: {
      id: PreCheckinStep.TrainingSummary,
      next: () => ({ flow: trainingSummaryFlow, returnStep: PreCheckinStep.AfterTrainingSummary }),
      back: (variables) =>
        variables.visitStatus === VisitStatus.DRY_PRECHECKED_IN
          ? { nextStep: PreCheckinStep.Start }
          : { bubble: 'back' },
    },
    [PreCheckinStep.AfterTrainingSummary]: {
      id: PreCheckinStep.AfterTrainingSummary,
      next: (variables) =>
        variables.checkinProcessConfiguration?.hasFacialRecognition
          ? {
              flow: facialRecognitionFlow,
              returnStep: PreCheckinStep.AfterFacialRecognition,
            }
          : { nextStep: PreCheckinStep.Checkin },
      back: () => ({ nextStep: PreCheckinStep.TrainingSummary }),
    },
    [PreCheckinStep.AfterFacialRecognition]: {
      id: PreCheckinStep.AfterFacialRecognition,
      next: () => ({ nextStep: PreCheckinStep.Checkin }),
    },
    [PreCheckinStep.Checkin]: {
      id: PreCheckinStep.Checkin,
      next: () => ({ flow: checkinFlow, returnStep: PreCheckinStep.AfterCheckin }),
      exit: () => ({ bubble: 'exit' }),
    },
    [PreCheckinStep.AfterCheckin]: {
      id: PreCheckinStep.AfterCheckin,
      next: (variables) =>
        variables.checkinProcessConfiguration?.hasPrintBadge && variables.isCheckinSuccess
          ? { flow: printBadgeFlow, returnStep: PreCheckinStep.AfterPrintBadge }
          : { flow: checkinSummaryFlow, returnStep: PreCheckinStep.AfterCheckinSummary },
    },
    [PreCheckinStep.AfterPrintBadge]: {
      id: PreCheckinStep.AfterPrintBadge,
      next: () => ({ flow: checkinSummaryFlow, returnStep: PreCheckinStep.AfterCheckinSummary }),
    },
    [PreCheckinStep.AfterCheckinSummary]: {
      id: PreCheckinStep.AfterCheckinSummary,
      next: () => ({ complete: true }),
    },
  },
};
